import * as React from "react";
import Box from "@mui/material/Box";
import {
  ClickAwayListener,
  Popover,
  FormControl,
  InputAdornment,
  ListItemButton,
} from "@mui/material";
import { useHistory } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import styled from "@emotion/styled";
import {
  BodyMedium,
  BodySmall,
  H8TitleMedium,
  BodyLarge,
} from "../../components/StyledComponents/Typography/Typography.tsx";
import {
  OutlinedButton,
  StyledIconButton,
} from "../../components/StyledComponents/Buttons/AuradineButtons";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { GetAllOrgsSitesList } from "../../api/api.js";
import { StyledFormControl } from "../../components/StyledComponents/Inputs/Inputs.js";
import {
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { StyledOutlinedInput } from "../../components/StyledComponents/Inputs/Inputs.js";
import { get } from "react-hook-form";
import { se } from "date-fns/locale";
import { set } from "date-fns";
import { useTheme } from "../../ThemeContext/ThemeContext.js";

const StyledPopoverContent = styled(Box)(({ colors }) => ({
  display: "flex",
  flexDirection: "column",
  borderRadius: "1rem",
  boxShadow: "0px 2px 6px 2px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.3)",
}));

const StyledPopover = styled(Popover,)(({ colors }) => ({
  "& .MuiPaper-root": {
    borderRadius: "1rem", 
    backgroundColor: colors?.background?.popover, 
  },
}));

const StyledHeaderAndFooterBox = styled(Box)`
  padding: 1.5rem;
`;

const OrgOperationsCard = (props) => {
  const {
    open,
    handleClose,
    anchorRef,
    selectedOrg,
    setSelectedOrg,
    setSelectedSite,
    setSelectedGroup,
    orgNames,
  } = props;
  const { colors } = useTheme();
  const [orgData, setOrgData] = React.useState(
    orgNames.sort()
  );

  const [searchValue, setSearchValue] = React.useState("");

  React.useEffect(() => {
    setOrgData(orgNames.sort());
  }, [orgNames]);

  const onOrgSearch = (e) => {
    if (e.target.value === "") {
      setSearchValue("");
      setOrgData(orgNames);
      return;
    } else {
      setSearchValue(e.target.value);
      const orgsData = [...orgNames];
      const foundOrg = orgsData.filter((org) => {
        return org
          .toLowerCase()
          .includes(e.target.value.toLowerCase());
      });
      setOrgData(foundOrg);
    }
  };
  const inputRef = React.useRef("");
  const handleClearSearch = () => {
    inputRef.current.value = "";
    setSearchValue("");
    setOrgData(orgNames);
  };
  return (
    <StyledPopover
      colors={colors}
      sx={{
        zIndex: 9999,
        borderRadius: "1rem",
        padding: "2rem",
      }}
      open={open}
      anchorEl={anchorRef.current}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
    >
      <ClickAwayListener onClickAway={handleClose}>
        <StyledPopoverContent colors={colors}>
          <Box
            display="flex"
            flexDirection={"column"}
            padding="0.5rem"
            className={"search-box"}
          >
            {/* Search Input */}
            <StyledFormControl colors={colors}
              fullWidth
              sx={{ mb: 4 }}
              variant="standard"
            >
              <StyledOutlinedInput
                colors={colors}
                ref={inputRef}
                id="standard-adornment-amount"
                placeholder="Search for org"
                value={searchValue}
                onChange={(event) => onOrgSearch(event)}
                startAdornment={
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: colors.text.muted }} />
                  </InputAdornment>
                }
                endAdornment={
                  <InputAdornment position="end">
                    <StyledIconButton 
                      colors={colors}
                      onClick={() => {
                        handleClearSearch();
                      }}
                    >
                      <CloseIcon />
                    </StyledIconButton>
                  </InputAdornment>
                }
              />
            </StyledFormControl>

            {/* Groups List */}
            <Box
              display={"flex"}
              flexDirection={"column"}
              width={"22.5rem"}
              minWidth={"22.5rem"}
              maxHeight={"15rem"}
              minHeight={"15rem"}
              maxWidth={"45rem"}
              overflow={"auto"}
            >
              {orgNames &&
                Array.isArray(orgData) &&
                orgData.length > 0 &&
                orgData.map((org, orgIndex) => (
                  <React.Fragment key={orgIndex}>
                    <ListItemButton
                      selected={
                        org === selectedOrg ? true : false
                      }
                      key={orgIndex}
                      onClick={() => {
                        setSelectedOrg(org);
                        setSearchValue("");
                        setSelectedSite("");
                        setSelectedGroup("");
                        handleClose();
                      }}
                      sx={{
                        minHeight: "3rem",
                        maxHeight: "3rem",
                        "&:hover": {
                          backgroundColor:
                            colors?.hover?.muted,
                          borderRadius: "6.5rem",
                          cursor: "pointer",
                        },
                        "&.Mui-selected": {
                          backgroundColor: colors?.hover?.primary,
                          borderRadius: "6.5rem",
                        },
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <H8TitleMedium key={orgIndex}>
                        {org}
                      </H8TitleMedium>
                      {selectedOrg && selectedOrg === org && (
                        <CloseIcon
                          fontSize="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedOrg("");
                            setSelectedSite("");
                            setSelectedGroup("");
                          }}
                          style={{ cursor: "pointer" }} 
                          sx={{ color: colors?.text?.muted }} 
                        />
                      )}
                    </ListItemButton>
                  </React.Fragment>
                ))}
            </Box>
          </Box>
          <Box></Box>
        </StyledPopoverContent>
      </ClickAwayListener>
    </StyledPopover>
  );
};

const GroupOperationsCard = (props) => {
  const {
    open,
    handleClose,
    anchorRef,
    selectedSite,
    selectedGroup,
    setSelectedSite,
    setSelectedGroup,
    siteLists,
  } = props;
  const [siteAndGroups, setSiteAndGroups] =
    React.useState(siteLists);
  const { getAccessTokenSilently, isAuthenticated } =
    useAuth0();
  const { colors } = useTheme();
  const [searchValue, setSearchValue] = React.useState("");

  const onSitesAndGroupSearch = (e) => {
    if (e.target.value === "") {
      setSearchValue("");
      setSiteAndGroups(siteLists);
      return;
    } else {
      setSearchValue(e.target.value);
      const sitesData = [...siteLists];
      const foundSite = sitesData.filter((site) => {
        return (
          site?.siteName
            .toLowerCase()
            .includes(e.target.value.toLowerCase()) ||
          (site.groups &&
            site.groups.some((group) =>
              group.DGName.toLowerCase().includes(
                e.target.value.toLowerCase()
              )
            ))
        );
      });
      setSiteAndGroups(foundSite);
    }
  };

  React.useEffect(() => {
    setSiteAndGroups(siteLists);
  }, [siteLists]);

  const inputRef = React.useRef("");
  const handleClearSearch = () => {
    inputRef.current.value = "";
    setSearchValue("");
    setSiteAndGroups(siteLists?.siteList);
  };
  return (
    <StyledPopover
      colors={colors}
      sx={{
        zIndex: 9999,
        borderRadius: "1rem",
        padding: "2rem",
      }}
      open={open}
      anchorEl={anchorRef.current}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
    >
      <ClickAwayListener onClickAway={handleClose}>
        <StyledPopoverContent colors={colors}>
          <Box
            display="flex"
            flexDirection={"column"}
            padding="0.5rem"
            className={"search-box"}
          >
            {/* Search Input */}
            <StyledFormControl colors={colors}
              fullWidth
              sx={{ mb: 4 }}
              variant="standard"
            >
              <StyledOutlinedInput
                colors={colors}
                ref={inputRef}
                id="standard-adornment-amount"
                placeholder="Search for sites or groups"
                value={searchValue}
                onChange={(event) =>
                  onSitesAndGroupSearch(event)
                }
                startAdornment={
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: colors.text.muted }} />
                  </InputAdornment>
                }
                endAdornment={
                  <InputAdornment position="end">
                    <StyledIconButton 
                      colors={colors}
                      onClick={() => {
                        handleClearSearch();
                      }}
                    >
                      <CloseIcon />
                    </StyledIconButton>
                  </InputAdornment>
                }
              />
            </StyledFormControl>

            {/* Groups List */}
            <Box
              display={"flex"}
              flexDirection={"column"}
              width={"22.5rem"}
              minWidth={"22.5rem"}
              maxHeight={"15rem"}
              minHeight={"15rem"}
              maxWidth={"45rem"}
              overflow={"auto"}
            >
              {siteAndGroups &&
                Array.isArray(siteAndGroups) &&
                siteAndGroups.length > 0 &&
                siteAndGroups.map((site, siteIndex) => (
                  <React.Fragment key={siteIndex}>
                    <ListItemButton
                      selected={
                        site.siteName === selectedSite &&
                        selectedGroup === ""
                      }
                      key={siteIndex}
                      onClick={() => {
                        setSelectedSite(site.siteName);
                        setSelectedGroup("");
                        handleClose();
                      }}
                      sx={{
                        minHeight: "3rem",
                        maxHeight: "3rem",
                        "&:hover": {
                          backgroundColor:
                            colors?.hover?.muted,
                          borderRadius: "6.5rem",
                          cursor: "pointer",
                        },
                        "&.Mui-selected": {
                          backgroundColor: colors?.hover?.primary,
                          borderRadius: "6.5rem",
                        },
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <H8TitleMedium key={siteIndex}>
                        {site.siteName}
                      </H8TitleMedium>
                      {selectedSite && selectedSite === site.siteName && !selectedGroup && (
                        <CloseIcon
                          fontSize="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedSite("");
                            setSelectedGroup("");
                          }}
                          style={{ cursor: "pointer" }} 
                          sx={{ color: colors?.text?.muted }}
                        />
                      )}
                    </ListItemButton>

                    {site.groups &&
                      site.groups.length > 0 &&
                      site.groups.map(
                        (group, groupIndex) => (
                          <React.Fragment key={groupIndex}>
                            <ListItemButton
                              selected={
                                group.DGName ===
                                  selectedGroup &&
                                site.siteName ===
                                  selectedSite &&
                                selectedGroup !== ""
                              }
                              key={groupIndex}
                              onClick={() => {
                                setSelectedSite(
                                  site.siteName
                                );
                                setSelectedGroup(
                                  group.DGName
                                );
                                handleClose();
                              }}
                              sx={{
                                paddingLeft: "2.5rem",
                                minHeight: "3rem",
                                maxHeight: "3rem",
                                "&:hover": {
                                  backgroundColor:
                                    colors?.hover?.muted,
                                  borderRadius: "6.5rem",
                                  cursor: "pointer",
                                },
                                "&.Mui-selected": {
                                  backgroundColor: colors?.hover?.primary,
                                  borderRadius: "6.5rem",
                                },
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <H8TitleMedium
                              key={groupIndex}
                              style={{ fontWeight: "400" }}
                            >
                              {group.DGName}
                            </H8TitleMedium>
                              {selectedSite &&
                                selectedGroup &&
                                selectedSite === site.siteName &&
                                selectedGroup === group.DGName && (
                                  <CloseIcon
                                    fontSize="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedSite("");
                                      setSelectedGroup("");
                                    }}
                                    style={{ cursor: "pointer" }} 
                                    sx={{ color: colors?.text?.muted }}
                                  />
                                )}
                            </ListItemButton>
                          </React.Fragment>
                        )
                      )}
                  </React.Fragment>
                ))}
            </Box>
          </Box>
          <Box></Box>
        </StyledPopoverContent>
      </ClickAwayListener>
    </StyledPopover>
  );
};

export default GroupOperationsCard;

export const GroupOperationsTab = (props) => {
  const {
    selectedSite,
    selectedGroup,
    selectedOrg,
    setSelectedOrg,
    setSelectedSite,
    setSelectedGroup,
    user,
    siteLists,
    isDashboardDataFetching,
    isDashboardDataLoading,
    isDashboardDataRefetching
  } = props;
  const [anchorEl, setAnchorEl] = React.useState(null);
  const { colors } = useTheme();

  const [siteAndGroups, setSiteAndGroups] =
    React.useState();

  const anchorRef = React.useRef(null);
  const orgAnchorRef = React.useRef(null);

  const [open, setOpen] = React.useState(false);
  const [orgOpen, setOrgOpen] = React.useState(false);
  const navigate = useHistory();
  const handleClick = (event) => {
    event.stopPropagation();
    setOpen((prevOpen) => !prevOpen);
  };
  const handleOrgClick = (event) => {
    event.stopPropagation();
    setOrgOpen((prevOpen) => !prevOpen);
  };

  const orgNames =
    siteLists &&
    siteLists.orgList &&
    Array.isArray(siteLists.orgList) &&
    siteLists.orgList.length > 0
      ? siteLists.orgList.map((org) => org.org_name)
      : [];

  React.useEffect(() => {
    if (
      !!selectedOrg &&
      Array.isArray(siteLists?.orgList) &&
      siteLists?.orgList.length > 0
    ) {
      const siteAndGroupData = siteLists.orgList.find(
        (org) => org.org_name === selectedOrg
      ).siteList;
      setSiteAndGroups(siteAndGroupData);
    }
  }, [selectedOrg, siteLists]);

  const handleClose = () => {
    setOpen(false);
  };
  return (
    <React.Fragment>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          textAlign: "center",
        }}
        gap={2}
      >
        <OutlinedButton
          colors={colors}
          ref={orgAnchorRef}
          onClick={handleOrgClick}
          // disabled={!!selectedOrg}
          disabled={isDashboardDataFetching || isDashboardDataLoading || isDashboardDataRefetching}
          endIcon={
            orgOpen ? (
              <KeyboardArrowUpRoundedIcon />
            ) : (
              <KeyboardArrowDownRoundedIcon />
            )
          }
        >
          {selectedOrg !== "" ? (
            <BodyMedium>{selectedOrg}</BodyMedium>
          ) : (
            <BodySmall>Select org</BodySmall>
          )}
        </OutlinedButton>

        <OutlinedButton
          colors={colors}
          ref={anchorRef}
          onClick={handleClick}
          disabled={!selectedOrg || isDashboardDataFetching || isDashboardDataLoading || isDashboardDataRefetching}
          endIcon={
            open ? (
              <KeyboardArrowUpRoundedIcon />
            ) : (
              <KeyboardArrowDownRoundedIcon />
            )
          }
        >
          {selectedSite !== "" && selectedGroup !== "" ? (
            <BodySmall>
              {selectedSite} - {selectedGroup}
            </BodySmall>
          ) : selectedSite !== "" &&
            selectedGroup === "" ? (
            <BodySmall>{selectedSite}</BodySmall>
          ) : (
            <BodySmall>Select site or group</BodySmall>
          )}
        </OutlinedButton>
      </Box>

      <OrgOperationsCard
        open={orgOpen}
        handleClose={() => setOrgOpen(false)}
        anchorRef={orgAnchorRef}
        selectedOrg={selectedOrg}
        setSelectedOrg={setSelectedOrg}
        setSelectedSite={setSelectedSite}
        setSelectedGroup={setSelectedGroup}
        orgNames={orgNames}
        user={user}
      />

      <GroupOperationsCard
        open={open}
        handleClose={handleClose}
        anchorRef={anchorRef}
        selectedOrg={selectedOrg}
        setSelectedOrg={setSelectedOrg}
        selectedSite={selectedSite}
        selectedGroup={selectedGroup}
        setSelectedSite={setSelectedSite}
        setSelectedGroup={setSelectedGroup}
        user={user}
        siteLists={siteAndGroups}
      />
    </React.Fragment>
  );
};
